import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { FadeUp } from "../components/Reveal";
import { EmSwash } from "../components/EmSwash";
import { LegalContactCard, formatLegalDate } from "../components/LegalContactCard";
import { LegalToc, LegalTocMobile } from "../components/LegalToc";
import { CreateCampaign } from "../components/CreateCampaign";
import { DonateComingSoonDialog } from "../components/DonateComingSoonDialog";
import { SEO } from "../lib/SEO";
import { EXPENSE_GUIDELINES } from "../data/content";

// Injected at build time by craco.config.js from this file's last git commit
// (or today's date while it has uncommitted edits). Edit the Terms, and the
// "Last updated" line moves on its own.
const TERMS_LAST_UPDATED = formatLegalDate(process.env.REACT_APP_TERMS_UPDATED);

/**
 * Terms & Conditions — legal boilerplate rendered in the site's editorial
 * type system. Copy is authoritative (do not edit prose without legal
 * review); layout is presentational only. Mirrors PrivacyPolicy.jsx's
 * typography so the two docs feel like companion volumes.
 */

const H2 = ({ children, id }) => (
  <h2
    id={id}
    className="mt-16 scroll-mt-28 border-t border-border pt-10 font-serif text-3xl leading-tight tracking-tight text-ink"
  >
    {children}
  </h2>
);

const H3 = ({ children, id }) => (
  <h3
    id={id}
    className="mt-10 text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine"
  >
    {children}
  </h3>
);

const P = ({ children }) => (
  <p className="mt-4 text-base leading-relaxed text-ink/85">{children}</p>
);

const UL = ({ children }) => (
  <ul className="mt-4 list-disc space-y-2 pl-6 text-base leading-relaxed text-ink/85 marker:text-tangerine">
    {children}
  </ul>
);

const Term = ({ children }) => (
  <span className="font-semibold text-ink">{children}</span>
);

export default function Terms() {
  const [createOpen, setCreateOpen] = useState(false);
  const [donateOpen, setDonateOpen] = useState(false);
  return (
    <div className="min-h-screen bg-sand">
      <SEO
        title="Terms & Conditions — My Rich Aunt"
        description="Terms of use for My Rich Aunt — the 501(c)(3) platform for direct, tax-deductible grants to verified college students."
        path="/terms"
      />
      <Navbar onStart={() => setCreateOpen(true)} onDonate={() => setDonateOpen(true)} />

      <main className="mx-auto max-w-6xl px-6 py-16 lg:px-8 lg:py-24 xl:grid xl:grid-cols-[13rem_minmax(0,48rem)] xl:justify-center xl:gap-16">
        <LegalToc testid="terms-toc" />
        <article id="legal-article" className="min-w-0 max-w-3xl">
        <FadeUp>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[13px] font-semibold uppercase tracking-[0.2em] text-olive transition-colors hover:text-tangerine"
            data-testid="terms-back-home"
          >
            <ArrowLeft className="h-3.5 w-3.5" strokeWidth={2} />
            Back home
          </Link>

          <span className="mt-8 block text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine">
            Legal
          </span>
          <h1 className="mt-4 font-serif text-3xl lg:text-[3.5rem] leading-[0.95] tracking-tight text-ink" data-testid="terms-heading">
            Terms &amp; <EmSwash>Conditions.</EmSwash>
          </h1>
          <p className="mt-5 text-[13px] font-medium uppercase tracking-[0.16em] text-olive" data-testid="terms-last-updated">
            Last updated: {TERMS_LAST_UPDATED}
          </p>
          <LegalContactCard topic="these terms" testid="terms-contact-card" />
          <LegalTocMobile testid="terms-toc-mobile" />

          <P>
            Please read these terms and conditions carefully before using Our
            Service.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="interpretation-and-definitions">Interpretation and Definitions</H2>

          <H3>Interpretation</H3>
          <P>
            The words of which the initial letter is capitalized have meanings
            defined under the following conditions. The following definitions
            shall have the same meaning regardless of whether they appear in
            singular or in plural.
          </P>

          <H3>Definitions</H3>
          <P>For the purposes of these Terms and Conditions:</P>
          <UL>
            <li>
              <Term>Affiliate</Term> means an entity that controls, is
              controlled by or is under common control with a party, where
              "control" means ownership of 50% or more of the shares, equity
              interest or other securities entitled to vote for election of
              directors or other managing authority.
            </li>
            <li>
              <Term>Account</Term> means a unique account created for You to
              access our Service or parts of our Service.
            </li>
            <li>
              <Term>Country</Term> refers to: United States
            </li>
            <li>
              <Term>Company</Term> (referred to as either "the Company", "We",
              "Us" or "Our" in this Agreement) refers to My Rich Aunt, Inc.,
              445 North Rossmore Avenue #121.
            </li>
            <li>
              <Term>Content</Term> refers to content such as text, images, or
              other information that can be posted, uploaded, linked to or
              otherwise made available by You, regardless of the form of that
              content.
            </li>
            <li>
              <Term>Device</Term> means any device that can access the Service
              such as a computer, a cellphone or a digital tablet.
            </li>
            <li>
              <Term>Feedback</Term> means feedback, innovations or suggestions
              sent by You regarding the attributes, performance or features of
              our Service.
            </li>
            <li>
              <Term>Service</Term> refers to the Website.
            </li>
            <li>
              <Term>Terms and Conditions</Term> (also referred as "Terms") mean
              these Terms and Conditions that form the entire agreement between
              You and the Company regarding the use of the Service.
            </li>
            <li>
              <Term>Third-party Social Media Service</Term> means any services
              or content (including data, information, products or services)
              provided by a third-party that may be displayed, included or made
              available by the Service.
            </li>
            <li>
              <Term>Website</Term> refers to My Rich Aunt, accessible from{" "}
              <a
                href="http://www.myrichaunt.org"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-tangerine underline underline-offset-4 hover:text-cobalt"
              >
                http://www.myrichaunt.org
              </a>
            </li>
            <li>
              <Term>You</Term> means the individual accessing or using the
              Service, or the company, or other legal entity on behalf of which
              such individual is accessing or using the Service, as applicable.
            </li>
          </UL>
        </FadeUp>

        <FadeUp>
          <H2 id="acknowledgment">Acknowledgment</H2>
          <P>
            These are the Terms and Conditions governing the use of this
            Service and the agreement that operates between You and the
            Company. These Terms and Conditions set out the rights and
            obligations of all users regarding the use of the Service.
          </P>
          <P>
            Your access to and use of the Service is conditioned on Your
            acceptance of and compliance with these Terms and Conditions.
            These Terms and Conditions apply to all visitors, users and others
            who access or use the Service.
          </P>
          <P>
            By accessing or using the Service You agree to be bound by these
            Terms and Conditions. If You disagree with any part of these Terms
            and Conditions then You may not access the Service.
          </P>
          <P>
            You represent that you are over the age of 18. The Company does
            not permit those under 18 to use the Service.
          </P>
          <P>
            Your access to and use of the Service is also conditioned on Your
            acceptance of and compliance with the Privacy Policy of the
            Company. Our{" "}
            <Link
              to="/privacy"
              className="font-medium text-tangerine underline underline-offset-4 hover:text-cobalt"
            >
              Privacy Policy
            </Link>{" "}
            describes Our policies and procedures on the collection, use and
            disclosure of Your personal information when You use the
            Application or the Website and tells You about Your privacy rights
            and how the law protects You. Please read Our Privacy Policy
            carefully before using Our Service.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="user-accounts">User Accounts</H2>
          <P>
            When You create an account with Us, You must provide Us
            information that is accurate, complete, and current at all times.
            Failure to do so constitutes a breach of the Terms, which may
            result in immediate termination of Your account on Our Service.
          </P>
          <P>
            You are responsible for safeguarding the password that You use to
            access the Service and for any activities or actions under Your
            password, whether Your password is with Our Service or a
            Third-Party Social Media Service.
          </P>
          <P>
            You agree not to disclose Your password to any third party. You
            must notify Us immediately upon becoming aware of any breach of
            security or unauthorized use of Your account.
          </P>
          <P>
            You may not use as a username the name of another person or entity
            or that is not lawfully available for use, a name or trademark
            that is subject to any rights of another person or entity other
            than You without appropriate authorization, or a name that is
            otherwise offensive, vulgar or obscene.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="campaign-eligibility">Campaign Eligibility</H2>
          <P>
            Fundraising campaigns on the Service may be created <Term>only by
            the enrolled student who will personally receive the grant.</Term>{" "}
            The Company does not permit parents, siblings, guardians,
            spouses, friends, classmates, teachers, employers, or any other
            third party to create, submit, or manage a campaign on behalf of a
            student. Any account identified as having been created by someone
            other than the beneficiary student will be suspended immediately
            and funds raised from donors who stated a non-binding preference
            for contributing toward the suspended campaign will be used to
            support other eligible campaigns.
          </P>
          <P>
            At the time a campaign is created, the student must certify under
            their own name that (i) they are the beneficiary of the campaign,
            (ii) they are the person creating and submitting the campaign,
            (iii) they are at least 18 years of age, and (iv) they are
            currently enrolled at an accredited U.S. college or university.
            These certifications are legally binding and become part of the
            campaign record.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="expense-guidelines">Expense Guidelines</H2>
          <P>
            Grants issued through the Company&rsquo;s supplemental educational
            grant program may be used <Term>only for education-related and
            student-living costs incurred while enrolled that are not already
            covered by financial aid.</Term>{" "}
            For grants of $500 or less the Company relies on the student&rsquo;s
            legally binding certification made at signup; larger grants may
            require documentation of expenses at the Company&rsquo;s
            discretion. Use of grant funds for a prohibited expense is a breach
            of these Terms and may result in suspension of the student&rsquo;s
            account and campaign.
          </P>
          <div className="mt-6 space-y-6 text-base leading-relaxed text-ink/85" data-testid="terms-expense-guidelines">
            {EXPENSE_GUIDELINES}
          </div>
        </FadeUp>

        <FadeUp>
          <H2 id="right-to-post-content">Your Right to Post Content</H2>
          <P>
            Our Service allows You to post Content. You are responsible for
            the Content that You post to the Service, including its legality,
            reliability, and appropriateness.
          </P>
          <P>
            By posting Content to the Service, You grant Us the right and
            license to use, modify, publicly perform, publicly display,
            reproduce, and distribute such Content on and through the Service.
            You retain any and all of Your rights to any Content You submit,
            post or display on or through the Service and You are responsible
            for protecting those rights. You agree that this license includes
            the right for Us to make Your Content available to other users of
            the Service, who may also use Your Content subject to these Terms.
          </P>
          <P>
            You represent and warrant that: (i) the Content is Yours (You own
            it) or You have the right to use it and grant Us the rights and
            license as provided in these Terms, and (ii) the posting of Your
            Content on or through the Service does not violate the privacy
            rights, publicity rights, copyrights, contract rights or any other
            rights of any person.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="content-restrictions">Content Restrictions</H2>
          <P>
            The Company is not responsible for the content of the Service's
            users. You expressly understand and agree that You are solely
            responsible for the Content and for all activity that occurs under
            your account, whether done so by You or any third person using
            Your account.
          </P>
          <P>
            You may not transmit any Content that is unlawful, offensive,
            upsetting, intended to disgust, threatening, libelous, defamatory,
            obscene or otherwise objectionable. Examples of such objectionable
            Content include, but are not limited to, the following:
          </P>
          <UL>
            <li>Unlawful or promoting unlawful activity.</li>
            <li>
              Defamatory, discriminatory, or mean-spirited content, including
              references or commentary about religion, race, sexual
              orientation, gender, national/ethnic origin, or other targeted
              groups.
            </li>
            <li>
              Spam, machine &ndash; or randomly &ndash; generated, constituting
              unauthorized or unsolicited advertising, chain letters, any
              other form of unauthorized solicitation, or any form of lottery
              or gambling.
            </li>
            <li>
              Containing or installing any viruses, worms, malware, trojan
              horses, or other content that is designed or intended to
              disrupt, damage, or limit the functioning of any software,
              hardware or telecommunications equipment or to damage or obtain
              unauthorized access to any data or other information of a third
              person.
            </li>
            <li>
              Infringing on any proprietary rights of any party, including
              patent, trademark, trade secret, copyright, right of publicity or
              other rights.
            </li>
            <li>
              Impersonating any person or entity including the Company and its
              employees or representatives.
            </li>
            <li>Violating the privacy of any third person.</li>
            <li>False information and features.</li>
          </UL>
          <P>
            The Company reserves the right, but not the obligation, to
            determine whether or not any Content is appropriate and complies
            with this Terms, refuse or remove this Content. The Company
            further reserves the right to make formatting and edits and change
            the manner of any Content. The Company can also limit or revoke
            the use of the Service if You post such objectionable Content. As
            the Company cannot control all content posted by users and/or
            third parties on the Service, you agree to use the Service at your
            own risk. You understand that by using the Service You may be
            exposed to content that You may find offensive, indecent,
            incorrect or objectionable, and You agree that under no
            circumstances will the Company be liable in any way for any
            content, including any errors or omissions in any content, or any
            loss or damage of any kind incurred as a result of your use of any
            content.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="content-backups">Content Backups</H2>
          <P>
            Although regular backups of Content are performed, the Company
            does not guarantee there will be no loss or corruption of data.
          </P>
          <P>
            Corrupt or invalid backup points may be caused by, without
            limitation, Content that is corrupted prior to being backed up or
            that changes during the time a backup is performed.
          </P>
          <P>
            The Company will provide support and attempt to troubleshoot any
            known or discovered issues that may affect the backups of Content.
            But You acknowledge that the Company has no liability related to
            the integrity of Content or the failure to successfully restore
            Content to a usable state.
          </P>
          <P>
            You agree to maintain a complete and accurate copy of any Content
            in a location independent of the Service.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="copyright-policy">Copyright Policy</H2>

          <H3>Intellectual Property Infringement</H3>
          <P>
            We respect the intellectual property rights of others. It is Our
            policy to respond to any claim that Content posted on the Service
            infringes a copyright or other intellectual property infringement
            of any person.
          </P>
          <P>
            If You are a copyright owner, or authorized on behalf of one, and
            You believe that the copyrighted work has been copied in a way
            that constitutes copyright infringement that is taking place
            through the Service, You must submit Your notice in writing to
            the attention of our copyright agent via email at{" "}
            <a
              href="mailto:hellofrom@myrichaunt.org"
              className="font-medium text-tangerine underline underline-offset-4 hover:text-cobalt"
            >
              hellofrom@myrichaunt.org
            </a>{" "}
            and include in Your notice a detailed description of the alleged
            infringement.
          </P>
          <P>
            You may be held accountable for damages (including costs and
            attorneys' fees) for misrepresenting that any Content is
            infringing Your copyright.
          </P>

          <H3>DMCA Notice and DMCA Procedure for Copyright Infringement Claims</H3>
          <P>
            You may submit a notification pursuant to the Digital Millennium
            Copyright Act (DMCA) by providing our Copyright Agent with the
            following information in writing (see 17 U.S.C 512(c)(3) for
            further detail):
          </P>
          <UL>
            <li>
              An electronic or physical signature of the person authorized to
              act on behalf of the owner of the copyright's interest.
            </li>
            <li>
              A description of the copyrighted work that You claim has been
              infringed, including the URL (i.e., web page address) of the
              location where the copyrighted work exists or a copy of the
              copyrighted work.
            </li>
            <li>
              Identification of the URL or other specific location on the
              Service where the material that You claim is infringing is
              located.
            </li>
            <li>Your address, telephone number, and email address.</li>
            <li>
              A statement by You that You have a good faith belief that the
              disputed use is not authorized by the copyright owner, its
              agent, or the law.
            </li>
            <li>
              A statement by You, made under penalty of perjury, that the
              above information in Your notice is accurate and that You are
              the copyright owner or authorized to act on the copyright
              owner's behalf.
            </li>
          </UL>
          <P>
            You can contact our copyright agent via email at{" "}
            <a
              href="mailto:hellofrom@myrichaunt.org"
              className="font-medium text-tangerine underline underline-offset-4 hover:text-cobalt"
            >
              hellofrom@myrichaunt.org
            </a>
            . Upon receipt of a notification, the Company will take whatever
            action, in its sole discretion, it deems appropriate, including
            removal of the challenged content from the Service.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="intellectual-property">Intellectual Property</H2>
          <P>
            The Service and its original content (excluding Content provided
            by You or other users), features and functionality are and will
            remain the exclusive property of the Company and its licensors.
          </P>
          <P>
            The Service is protected by copyright, trademark, and other laws
            of both the Country and foreign countries.
          </P>
          <P>
            Our trademarks and trade dress may not be used in connection with
            any product or service without the prior written consent of the
            Company.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="your-feedback">Your Feedback to Us</H2>
          <P>
            You assign all rights, title and interest in any Feedback You
            provide the Company. If for any reason such assignment is
            ineffective, You agree to grant the Company a non-exclusive,
            perpetual, irrevocable, royalty free, worldwide right and license
            to use, reproduce, disclose, sub-license, distribute, modify and
            exploit such Feedback without restriction.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="links-to-other-websites">Links to Other Websites</H2>
          <P>
            Our Service may contain links to third-party web sites or services
            that are not owned or controlled by the Company.
          </P>
          <P>
            The Company has no control over, and assumes no responsibility
            for, the content, privacy policies, or practices of any third
            party web sites or services. You further acknowledge and agree
            that the Company shall not be responsible or liable, directly or
            indirectly, for any damage or loss caused or alleged to be caused
            by or in connection with the use of or reliance on any such
            content, goods or services available on or through any such web
            sites or services.
          </P>
          <P>
            We strongly advise You to read the terms and conditions and
            privacy policies of any third-party web sites or services that You
            visit.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="termination">Termination</H2>
          <P>
            We may terminate or suspend Your Account immediately, without
            prior notice or liability, for any reason whatsoever, including
            without limitation if You breach these Terms and Conditions.
          </P>
          <P>
            Upon termination, Your right to use the Service will cease
            immediately. If You wish to terminate Your Account, You may simply
            discontinue using the Service.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="limitation-of-liability">Limitation of Liability</H2>
          <P>
            Notwithstanding any damages that You might incur, the entire
            liability of the Company and any of its suppliers under any
            provision of this Terms and Your exclusive remedy for all of the
            foregoing shall be limited to the amount actually paid by You
            through the Service or 100 USD if You haven't purchased anything
            through the Service.
          </P>
          <P>
            To the maximum extent permitted by applicable law, in no event
            shall the Company or its suppliers be liable for any special,
            incidental, indirect, or consequential damages whatsoever
            (including, but not limited to, damages for loss of profits, loss
            of data or other information, for business interruption, for
            personal injury, loss of privacy arising out of or in any way
            related to the use of or inability to use the Service, third-party
            software and/or third-party hardware used with the Service, or
            otherwise in connection with any provision of this Terms), even
            if the Company or any supplier has been advised of the
            possibility of such damages and even if the remedy fails of its
            essential purpose.
          </P>
          <P>
            Some states do not allow the exclusion of implied warranties or
            limitation of liability for incidental or consequential damages,
            which means that some of the above limitations may not apply. In
            these states, each party's liability will be limited to the
            greatest extent permitted by law.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="as-is-disclaimer">"AS IS" and "AS AVAILABLE" Disclaimer</H2>
          <P>
            The Service is provided to You "AS IS" and "AS AVAILABLE" and with
            all faults and defects without warranty of any kind. To the
            maximum extent permitted under applicable law, the Company, on
            its own behalf and on behalf of its Affiliates and its and their
            respective licensors and service providers, expressly disclaims
            all warranties, whether express, implied, statutory or otherwise,
            with respect to the Service, including all implied warranties of
            merchantability, fitness for a particular purpose, title and
            non-infringement, and warranties that may arise out of course of
            dealing, course of performance, usage or trade practice. Without
            limitation to the foregoing, the Company provides no warranty or
            undertaking, and makes no representation of any kind that the
            Service will meet Your requirements, achieve any intended results,
            be compatible or work with any other software, applications,
            systems or services, operate without interruption, meet any
            performance or reliability standards or be error free or that any
            errors or defects can or will be corrected.
          </P>
          <P>
            Without limiting the foregoing, neither the Company nor any of the
            company's provider makes any representation or warranty of any
            kind, express or implied: (i) as to the operation or availability
            of the Service, or the information, content, and materials or
            products included thereon; (ii) that the Service will be
            uninterrupted or error-free; (iii) as to the accuracy, reliability,
            or currency of any information or content provided through the
            Service; or (iv) that the Service, its servers, the content, or
            e-mails sent from or on behalf of the Company are free of viruses,
            scripts, trojan horses, worms, malware, timebombs or other harmful
            components.
          </P>
          <P>
            Some jurisdictions do not allow the exclusion of certain types of
            warranties or limitations on applicable statutory rights of a
            consumer, so some or all of the above exclusions and limitations
            may not apply to You. But in such a case the exclusions and
            limitations set forth in this section shall be applied to the
            greatest extent enforceable under applicable law.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="governing-law">Governing Law</H2>
          <P>
            The laws of the Country, excluding its conflicts of law rules,
            shall govern this Terms and Your use of the Service. Your use of
            the Application may also be subject to other local, state,
            national, or international laws.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="dispute-resolution">Dispute Resolution</H2>
          <P>
            If You have any concern or dispute about the Service, You agree to
            first try to resolve the dispute informally by contacting the
            Company.
          </P>

          <H3>For European Union (EU) Users</H3>
          <P>
            If You are a European Union consumer, you will benefit from any
            mandatory provisions of the law of the country in which you are
            resident in.
          </P>

          <H3>United States Legal Compliance</H3>
          <P>
            You represent and warrant that (i) You are not located in a
            country that is subject to the United States government embargo,
            or that has been designated by the United States government as a
            "terrorist supporting" country, and (ii) You are not listed on
            any United States government list of prohibited or restricted
            parties.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="severability-and-waiver">Severability and Waiver</H2>

          <H3>Severability</H3>
          <P>
            If any provision of these Terms is held to be unenforceable or
            invalid, such provision will be changed and interpreted to
            accomplish the objectives of such provision to the greatest extent
            possible under applicable law and the remaining provisions will
            continue in full force and effect.
          </P>

          <H3>Waiver</H3>
          <P>
            Except as provided herein, the failure to exercise a right or to
            require performance of an obligation under these Terms shall not
            effect a party's ability to exercise such right or require such
            performance at any time thereafter nor shall the waiver of a
            breach constitute a waiver of any subsequent breach.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="translation-interpretation">Translation Interpretation</H2>
          <P>
            These Terms and Conditions may have been translated if We have
            made them available to You on our Service. You agree that the
            original English text shall prevail in the case of a dispute.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="changes-to-these-terms">Changes to These Terms and Conditions</H2>
          <P>
            We reserve the right, at Our sole discretion, to modify or replace
            these Terms at any time. If a revision is material We will make
            reasonable efforts to provide at least 30 days' notice prior to
            any new terms taking effect. What constitutes a material change
            will be determined at Our sole discretion.
          </P>
          <P>
            By continuing to access or use Our Service after those revisions
            become effective, You agree to be bound by the revised terms. If
            You do not agree to the new terms, in whole or in part, please
            stop using the website and the Service.
          </P>
        </FadeUp>

        <FadeUp>
          <H2 id="contact-us">Contact Us</H2>
          <P>
            If you have any questions about these Terms and Conditions, You
            can contact us:
          </P>
          <UL>
            <li>
              By email:{" "}
              <a
                href="mailto:hellofrom@myrichaunt.org"
                data-testid="terms-contact-email"
                className="font-medium text-tangerine underline underline-offset-4 hover:text-cobalt"
              >
                hellofrom@myrichaunt.org
              </a>
            </li>
          </UL>
        </FadeUp>
        </article>
      </main>

      <Footer variant="minimal" />

      <CreateCampaign open={createOpen} onOpenChange={setCreateOpen} />
      <DonateComingSoonDialog open={donateOpen} onOpenChange={setDonateOpen} />
    </div>
  );
}
